import React, { useState, useEffect, useCallback } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, RefreshControl, Modal } from 'react-native';
import { Card, Text, Avatar, Button, Chip, useTheme, ActivityIndicator, Snackbar, Surface } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { getActiveVisits, endVisit } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { formatDateTime } from '../../../utils/dateUtils';

const ActiveVisitsScreen = ({ navigation }) => {
  const theme = useTheme();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Ziyareti sonlandırma durumu
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [endingVisit, setEndingVisit] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Her saniye zamanı güncelle (süre ve ücret hesaplaması için)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchVisits = useCallback(async () => {
    try {
      setError(null);
      const result = await getActiveVisits();
      setVisits(result.data || []);
    } catch (err) {
      setError(err.message || 'Aktif ziyaretler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchVisits();
    }, [fetchVisits])
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchVisits();
  }, [fetchVisits]);

  const handleEndVisit = (visit) => {
    setSelectedVisit(visit);
    setConfirmDialogVisible(true);
  };

  const confirmEndVisit = async () => {
    setConfirmDialogVisible(false);
    
    if (!selectedVisit) return;
    
    try {
      setEndingVisit(true);
      const response = await endVisit(selectedVisit.id, '');
      
      if (response.success) {
        setSnackbarMessage('Ziyaret başarıyla sonlandırıldı');
        fetchVisits(); // Listeyi yenile
      } else {
        setSnackbarMessage('Ziyaret sonlandırılırken bir hata oluştu');
      }
    } catch (err) {
      setSnackbarMessage(err.message || 'Ziyaret sonlandırılırken bir hata oluştu');
    } finally {
      setEndingVisit(false);
      setSnackbarVisible(true);
    }
  };
  
  // Aktif ziyaretler için geçen süreyi hesaplama fonksiyonu (dakika olarak)
  const calculateElapsedTime = (startTime) => {
    const start = new Date(startTime);
    const now = currentTime;
    const elapsedMinutes = Math.floor((now - start) / (1000 * 60));
    
    return elapsedMinutes;
  };
  
  // Ücret formatını TL olarak göster
  const formatPrice = (price) => {
    return parseFloat(price).toFixed(2) + ' ₺';
  };
  
  // Tahmini ücret hesaplama
  const calculateEstimatedPrice = (visit) => {
    //console.log('Ziyaret için ücret hesaplanıyor:', visit.id);
    
    if (!visit.activityType) {
      //console.log('ActivityType bulunamadı!');
      return formatPrice(0);
    }
    
    //console.log('ActivityType değerleri:', 
      //'base_price:', visit.activityType.base_price, 
      //'price_per_minute:', visit.activityType.price_per_minute);
    
    const basePrice = parseFloat(visit.activityType.base_price || 0);
    const pricePerMinute = visit.activityType.price_per_minute !== undefined ? 
      parseFloat(visit.activityType.price_per_minute) : 0; 
    const elapsedMinutes = calculateElapsedTime(visit.start_time);
    
    //console.log('Hesaplama değerleri:', 
      //'basePrice:', basePrice, 
     // 'pricePerMinute:', pricePerMinute, 
      //'elapsedMinutes:', elapsedMinutes);
    
    // Giriş ücreti + (dakika * dakika ücreti)
    const totalPrice = basePrice + (elapsedMinutes * pricePerMinute);
    //console.log('Hesaplanan toplam ücret:', totalPrice);
    
    return formatPrice(totalPrice);
  };
  
  // Süreyi hesapla ve formatla (saat:dakika olarak)
  const calculateVisitDuration = (startTime) => {
    const minutes = calculateElapsedTime(startTime);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours} saat ${mins} dakika`;
    }
    return `${mins} dakika`;
  };

  const renderVisitItem = ({ item }) => {
    const visit = item;
    // API'den gelen child ve parent bilgilerini direkt kullan
    const childName = visit.child ? `${visit.child.first_name} ${visit.child.last_name}` : 'Bilinmiyor';
    
    // Veli adını parent_name alanından al, yoksa parent nesnesi üzerinden al
     // Veli adını doğrudan parent_name alanından al
     const parentName = visit && visit.child ? visit.child.parent_name || 'İsimsiz Ebeveyn' : 'İsimsiz Ebeveyn';
     const parentPhone = visit && visit.child ? visit.child.parent_phone || '-' : '-';
     
    
    // Avatar için çocuğun adının baş harfleri
    const avatarText = childName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
    
    return (
      <Card style={styles.card}>
        <Card.Content style={styles.cardContent}>
          <View style={styles.headerRow}>
            <Avatar.Text size={40} label={avatarText} style={styles.avatar} />
            <View style={styles.headerText}>
              <Text style={styles.childName}>{childName}</Text>
              <Text style={styles.parentName}>Veli: {parentName}</Text>
              <Text style={styles.parentPhone}>Tel:  {parentPhone}</Text>
            </View>
            <Chip mode="outlined" icon="clock-outline" style={styles.durationChip}>
              {calculateVisitDuration(visit.start_time)}
            </Chip>
          </View>
          
          <View style={styles.divider} />
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Giriş:</Text>
            <Text style={styles.detailValue}>{formatDateTime(visit.start_time)}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Süre:</Text>
            <Text style={styles.detailValue}>{calculateVisitDuration(visit.start_time)}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Ücret:</Text>
            <Text style={[styles.detailValue, styles.priceValue]}>
              {visit.end_time 
                ? formatPrice(visit.price)
                : calculateEstimatedPrice(visit) + ' (tahmini)'}
            </Text>
          </View>
          
          {visit.notes && (
            <View style={styles.notesContainer}>
              <Text style={styles.notesLabel}>Notlar:</Text>
              <Text style={styles.notesText}>{visit.notes}</Text>
            </View>
          )}
          
          <View style={styles.buttonContainer}>
            <Button 
              mode="contained" 
              onPress={() => handleEndVisit(visit)}
              color="#fff"
              loading={endingVisit && selectedVisit?.id === visit.id}
              disabled={endingVisit}
              style={styles.endButton}
            >
              Ziyareti Sonlandır
            </Button>
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Aktif ziyaretler yükleniyor...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={visits}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderVisitItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="calendar-clock" size={80} color="rgb(125, 76, 236)" />
            <Text style={styles.emptyText}>Aktif ziyaret bulunmuyor</Text>
          </View>
        }
      />
      
      <Modal
        visible={confirmDialogVisible}
        transparent={true}
        onRequestClose={() => setConfirmDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="timer-off-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.modalIcon} 
            />
            
            <Text style={styles.modalTitle}>Ziyareti Sonlandırma Onayı</Text>
            <Text style={styles.modalText}>
              {selectedVisit?.child_name} için ziyaret sonlandırma işlemini onaylıyor musunuz?
            </Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={confirmEndVisit}
                loading={endingVisit}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="timer-off"
                color="#fff"
              >
                Ziyareti Sonlandır
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setConfirmDialogVisible(false)}
                style={styles.modalButton}
                color="rgb(125, 76, 236)"
              >
                İptal
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    elevation: 5,
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  modalIcon: {
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    minWidth: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
  },
  listContent: {
    padding: 16,
    paddingBottom: 80, // FAB için extra padding
  },
  card: {
    marginBottom: 16,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  cardContent: {
    padding: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  avatar: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  headerText: {
    flex: 1,
    marginLeft: 16,
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  parentName: {
    fontSize: 14,
    color: '#757575',
  },
  parentPhone: {
    fontSize: 14,
    color: '#757575',
    marginTop: 2,
  },
  divider: {
    marginVertical: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 4,
  },
  detailLabel: {
    fontSize: 14,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
  },
  durationChip: {
    backgroundColor: 'rgba(125, 76, 236, 0.15)',
    borderColor: 'rgb(125, 76, 236)',
  },
  notesContainer: {
    marginVertical: 8,
  },
  notesLabel: {
    fontSize: 14,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  notesText: {
    fontSize: 14,
  },
  buttonContainer: {
    marginTop: 16,
  },
  endButton: {
    flex: 1,
    marginRight: 8,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    color: '#757575',
    marginTop: 16,
    textAlign: 'center',
  },
  priceValue: {
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
});

export default ActiveVisitsScreen;
